import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  Outlet,
  Route,
} from "react-router-dom";

import { OnboardingRoutes } from "@js/apps/auth/routes";
import { SignUpFinishPage } from "@js/apps/auth/views/finish";
import { InvitationRedirect } from "@js/apps/auth/views/invitation-redirect";
import { OnboardingOrganizationPage } from "@js/apps/auth/views/organization-page/organization-page";
import { SignUpEmailConfirmPage } from "@js/apps/auth/views/sign-up-email-confirm";
import { BookmarksPage } from "@js/apps/bookmarks/layout/bookmarks-layout";
import { JobsBookmarksPage } from "@js/apps/bookmarks/views/jobs-bookmarks";
import { BYOTClientMVPPage } from "@js/apps/byot/views/client-mvp";
import { SignUpBYOT } from "@js/apps/byot/views/sign-up-byot";
import { SignUpBYOTAcceptOffer } from "@js/apps/byot/views/sign-up-byot/accept-offer";
import {
  SignUpBYOTCongrats,
  SignUpBYOTCongratsAccessWrapper,
} from "@js/apps/byot/views/sign-up-byot/congrats";
import { CompleteProfilePage } from "@js/apps/complete-profile/views/complete-profile";
import { EmployerDashboardMyJobsPage } from "@js/apps/employer/views/dashboard/my-jobs";
import { EmployerDashboardMyTalentPage } from "@js/apps/employer/views/dashboard/my-talent";
import { EmployerDashboardWelcomePage } from "@js/apps/employer/views/dashboard/welcome";
import { ATSIntegrationPage } from "@js/apps/employer/views/edit-profile/ats-integration";
import { EmployerEditOrganizationInfoPage } from "@js/apps/employer/views/edit-profile/organization-info";
import { EmployerEditPersonalInfoPage } from "@js/apps/employer/views/edit-profile/personal-info";
import { EmployerTeamMembersPage } from "@js/apps/employer/views/edit-profile/team-members";
import { EmployerInvoicesPage } from "@js/apps/employer/views/invoices";
import {
  EmployerInvoiceBatchesViewLazy,
  EmployerInvoicesTableViewLazy,
} from "@js/apps/employer/views/lazy";
import { MyWorkIndexRedirect } from "@js/apps/freelancer/components/my-work-index-redirect";
import { FreelancerProfilePageRoutes } from "@js/apps/freelancer/routes/freelancer-profile";
import { SearchTalentPage } from "@js/apps/freelancer/views";
import { EarnPage } from "@js/apps/freelancer/views/dashboard/earn";
import { EarnIndexRedirectPage } from "@js/apps/freelancer/views/dashboard/earn/earn-index-redirect-page";
import { MyWalletPage } from "@js/apps/freelancer/views/dashboard/my-wallet";
import { MyOffersListingPage } from "@js/apps/freelancer/views/dashboard/offers";
import { EditInvoicePage } from "@js/apps/invoices/views/edit-invoice";
import { BidDrawer } from "@js/apps/jobs/apps/bids";
import {
  CreateJobRoutes,
  EditJobRoutes,
} from "@js/apps/jobs/apps/create-job/routes/";
import {
  RoleJobsPage,
  SearchJobsPage,
} from "@js/apps/jobs/apps/listing/views/";
import { OffersRoutes } from "@js/apps/jobs/apps/offers/routes";
import { EmbeddedJobsListPage } from "@js/apps/jobs/apps/public-listing/views/embedded-jobs-list";
import { InviteTalentDrawer } from "@js/apps/jobs/components";
import { AIInterviewRedirect } from "@js/apps/jobs/views/ai-interview-redirect";
import { InterviewQuestionsPage } from "@js/apps/jobs/views/interview-questions";
import { InterviewPage } from "@js/apps/jobs/views/interviews";
import JobsListing from "@js/apps/jobs/views/jobs-listing";
import { DataLabelingJobsListing } from "@js/apps/jobs/views/jobs-listing/jobs-listing";
import RejectionFeedbackPage from "@js/apps/jobs/views/rejection-feedback";
import { MessagesProvider } from "@js/apps/messenger/context";
import { MessagesPage } from "@js/apps/messenger/views/messages";
import { RosterDetailPage } from "@js/apps/rosters/views/detail";
import { RostersListPage } from "@js/apps/rosters/views/list";
import { AppProviders } from "@js/components/app-providers";
import { ErrorBoundary } from "@js/components/error-boundary";

import { ConfirmEmailChangePage } from "../apps/auth/views/confirm-email-change";
import ForgotPasswordPage from "../apps/auth/views/forgot-password";
import { AuthLoginPage } from "../apps/auth/views/login";
import AuthResetPasswordPage from "../apps/auth/views/reset-password";
import AccessDenied from "../apps/common/views/access-denied";
import { App } from "../apps/common/views/app";
import { ConfirmPage } from "../apps/common/views/confirm-page";
import PageNotFoundPage from "../apps/common/views/page-not-found";
import TooManyRequestsPage from "../apps/common/views/too-many-requests";
import { DashboardReferClientsPage } from "../apps/dashboard/views/refer-clients";
import DashboardReferClientsProgramPage from "../apps/dashboard/views/refer-clients-program";
import { DashboardReferTalentPage } from "../apps/dashboard/views/refer-talent";
import { EmployerProfilePage } from "../apps/employer/views/profile";
import { FreelancerDashboardBidsListingPage } from "../apps/freelancer/views/dashboard/applications/my-bids-listing";
import { FreelancerDashboardInvoicesPage } from "../apps/freelancer/views/dashboard/invoices";
import { DashboardMyJobsPage } from "../apps/freelancer/views/dashboard/jobs-history";
import { DashboardMyInvitesPage } from "../apps/freelancer/views/dashboard/my-invites";
import { FreelancerDashboardWelcomePage } from "../apps/freelancer/views/dashboard/welcome";
import { TalentListingPage } from "../apps/freelancer/views/talent-list";
import { AddInvoicePage } from "../apps/invoices/views/add-invoice";
import { FreelancerInvoicesListingPage } from "../apps/invoices/views/freelancer";
import { InviteTalentPage } from "../apps/jobs/views/invite-talent";
import JobBidsPage from "../apps/jobs/views/job-bids";
import JobDetailsPage from "../apps/jobs/views/job-details";
import { JobEditBidPage, JobNewBidPage } from "../apps/jobs/views/job-new-bid";
import SettingsAccountInfoPage from "../apps/settings/views/account-info";
import SettingsNotifications from "../apps/settings/views/notifications";
import SettingsPayments from "../apps/settings/views/payments";
import SettingsPrivacySecurity from "../apps/settings/views/privacy-security";
import { lazyWithRetry } from "../utils";

import AdminRoutes from "./admin-routes";
import { ACCESS_RULE } from "./constants";
import {
  Logout,
  ProtectedRoute,
  RedirectAuthenticatedUserFromLoginToNext,
} from "./protected-route";
import redirects from "./route-redirects";
import { UnknownRoute } from "./unknown-route";

const GrantTokenRewardPage = lazyWithRetry(
  () =>
    import(
      /* webpackChunkName: "admin-views" */ "../apps/admin/views/grant-btrust-reward"
    ),
);

const TALENT_MAIN_ACCESS_RULE = [
  ...ACCESS_RULE.ACCOUNT_ALL_SET,
  ACCESS_RULE.HAS_FREELANCER,
];

const EMPLOYER_MAIN_ACCESS_RULE = [
  ...ACCESS_RULE.ACCOUNT_ALL_SET,
  ACCESS_RULE.HAS_EMPLOYER,
];

export const routes = () => (
  <Route
    path="/"
    element={
      <ErrorBoundary>
        {/* // when not included inside router, using react-router elements (eg. Link) inside snackbar will throw error that it cannot be used outside of router scope */}
        <AppProviders>
          <App />
        </AppProviders>
      </ErrorBoundary>
    }
  >
    {redirects}
    {AdminRoutes}
    <Route
      path="grant_token_reward"
      element={
        <ProtectedRoute
          rules={[
            ACCESS_RULE.AUTHENTICATED,
            ACCESS_RULE.CAN_GRANT_TOKEN_REWARD,
          ]}
        >
          <GrantTokenRewardPage />
        </ProtectedRoute>
      }
    />
    <Route path="r/:referrer" element={<InvitationRedirect />} />
    <Route path="sign_up_byo_talent">
      <Route
        index
        element={
          <RedirectAuthenticatedUserFromLoginToNext>
            <ProtectedRoute rules={[ACCESS_RULE.NOT_AUTHENTICATED]}>
              <SignUpBYOT />
            </ProtectedRoute>
          </RedirectAuthenticatedUserFromLoginToNext>
        }
      />
      <Route
        path="accept_offer"
        element={
          <ProtectedRoute rules={[ACCESS_RULE.NEW_USER_HAS_PENDING_BYOT_OFFER]}>
            <SignUpBYOTAcceptOffer />
          </ProtectedRoute>
        }
      />
      <Route
        path="congrats"
        element={
          <SignUpBYOTCongratsAccessWrapper>
            {/* can be accessed only after accept_offer step */}
            <SignUpBYOTCongrats />
          </SignUpBYOTCongratsAccessWrapper>
        }
      />
    </Route>
    <Route path="auth">
      <Route index element={<Navigate to="login" />} />
      <Route
        path="login"
        element={
          <RedirectAuthenticatedUserFromLoginToNext>
            <ProtectedRoute rules={[ACCESS_RULE.NOT_AUTHENTICATED]}>
              <AuthLoginPage />
            </ProtectedRoute>
          </RedirectAuthenticatedUserFromLoginToNext>
        }
      />
      <Route path="sign_up/*" element={<Outlet />}>
        {OnboardingRoutes}
      </Route>
      <Route
        path="forgot_password"
        element={
          <ProtectedRoute rules={[ACCESS_RULE.NOT_AUTHENTICATED]}>
            <ForgotPasswordPage />
          </ProtectedRoute>
        }
      />
      <Route path="logout" element={<Logout />} />
    </Route>
    <Route
      path="verify_email"
      element={
        <ProtectedRoute
          rules={[
            ACCESS_RULE.AUTHENTICATED,
            ACCESS_RULE.ACCOUNT_TYPE_SELECTED,
            ACCESS_RULE.EMAIL_NOT_VERIFIED,
          ]}
        >
          <SignUpFinishPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="email_confirm"
      element={
        <ProtectedRoute
          rules={[
            ACCESS_RULE.ACCOUNT_TYPE_SELECTED,
            ACCESS_RULE.EMAIL_NOT_VERIFIED,
          ]}
        >
          <SignUpEmailConfirmPage />
        </ProtectedRoute>
      }
    />
    <Route path="confirm-email-change" element={<ConfirmEmailChangePage />} />
    <Route
      path="reset_password/:uid/:token"
      element={
        <ProtectedRoute rules={[ACCESS_RULE.NOT_AUTHENTICATED]}>
          <AuthResetPasswordPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="search-talent"
      element={
        <ProtectedRoute
          rules={[ACCESS_RULE.AUTHENTICATED, ACCESS_RULE.CAN_VIEW_TALENT_LIST]}
        >
          <SearchTalentPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="talent"
      element={
        <ProtectedRoute
          rules={[
            ...ACCESS_RULE.ACCOUNT_ALL_SET,
            ACCESS_RULE.CAN_VIEW_TALENT_LIST,
          ]}
        >
          <TalentListingPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="talent"
      element={
        <ProtectedRoute rules={[...TALENT_MAIN_ACCESS_RULE]}>
          <Outlet />
        </ProtectedRoute>
      }
    >
      <Route path="dashboard">
        <Route index element={<Navigate to="welcome" />} />
        <Route path="welcome" element={<FreelancerDashboardWelcomePage />} />
        <Route path="my_jobs">
          <Route index element={<MyWorkIndexRedirect />} />
          <Route path="history" element={<Outlet />}>
            <Route index element={<DashboardMyJobsPage />} />
            <Route path="*" element={<Navigate to="" replace />} />
          </Route>
          <Route path="invites" element={<DashboardMyInvitesPage />} />
          <Route path="my_proposals" element={<Outlet />}>
            <Route index element={<FreelancerDashboardBidsListingPage />} />
            <Route path="*" element={<Navigate to="" replace />} />
          </Route>

          <Route
            path="invoices"
            element={<FreelancerDashboardInvoicesPage />}
          />
          <Route path="offers" element={<Outlet />}>
            <Route index element={<MyOffersListingPage />} />
            <Route path="*" element={<Navigate to="" replace />} />
          </Route>
        </Route>
        <Route path="earn" element={<EarnPage />}>
          <Route index element={<EarnIndexRedirectPage />} />
          <Route path="refer_talent" element={<DashboardReferTalentPage />} />
          <Route path="refer_clients" element={<DashboardReferClientsPage />} />
          <Route
            path="refer_clients_program"
            element={<DashboardReferClientsProgramPage />}
          />
          <Route
            path="*"
            element={<Navigate to="/talent/dashboard/earn/" replace />}
          />
        </Route>
        <Route path="my_wallet" element={<MyWalletPage />} />
      </Route>
      <Route path="invoices">
        <Route index element={<FreelancerInvoicesListingPage />} />
        <Route path="add_new" element={<AddInvoicePage />} />
        <Route path="edit/:id" element={<EditInvoicePage />} />
      </Route>
    </Route>
    <Route path="talent/:id/*">{FreelancerProfilePageRoutes}</Route>

    <Route path="employer">
      <Route index element={<Navigate to="dashboard" />} />
      <Route
        path="dashboard"
        element={
          <ProtectedRoute rules={[...EMPLOYER_MAIN_ACCESS_RULE]}>
            <Outlet />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="welcome" />} />
        <Route
          index
          path="welcome"
          element={
            <ProtectedRoute rules={[ACCESS_RULE.EMPLOYER_HAS_ADDRESS_SET]}>
              <EmployerDashboardWelcomePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="my_jobs"
          element={
            <ProtectedRoute rules={[ACCESS_RULE.EMPLOYER_HAS_ADDRESS_SET]}>
              <EmployerDashboardMyJobsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="my_talent"
          element={
            <ProtectedRoute rules={[ACCESS_RULE.EMPLOYER_HAS_ADDRESS_SET]}>
              <EmployerDashboardMyTalentPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="my_talent/add"
          element={
            <ProtectedRoute
              rules={[
                ACCESS_RULE.EMPLOYER_HAS_ADDRESS_SET,
                ACCESS_RULE.IS_IMPERSONATED_SESSION,
              ]}
            >
              <BYOTClientMVPPage />
            </ProtectedRoute>
          }
        />
      </Route>
      <Route
        path="invoices"
        element={
          <ProtectedRoute rules={[ACCESS_RULE.CAN_VIEW_EMPLOYER_INVOICES]}>
            <EmployerInvoicesPage />
          </ProtectedRoute>
        }
      >
        <Route index element={<EmployerInvoicesTableViewLazy />} />
        <Route
          path="invoice_batches"
          element={<EmployerInvoiceBatchesViewLazy />}
        />
      </Route>
      <Route
        path="edit"
        element={
          <ProtectedRoute
            rules={[
              ...EMPLOYER_MAIN_ACCESS_RULE,
              ACCESS_RULE.EMPLOYER_HAS_ADDRESS_SET,
            ]}
          >
            <Outlet />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="personal_info" />} />
        <Route
          path="personal_info"
          element={<EmployerEditPersonalInfoPage />}
        />
        <Route
          path="organization_info"
          element={
            <ProtectedRoute rules={[ACCESS_RULE.CAN_MANAGE_EMPLOYER_ACCOUNT]}>
              <EmployerEditOrganizationInfoPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="team_members"
          element={
            <ProtectedRoute rules={[ACCESS_RULE.CAN_MANAGE_EMPLOYER_ACCOUNT]}>
              <EmployerTeamMembersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="ats_integration"
          element={
            <ProtectedRoute rules={[ACCESS_RULE.CAN_MANAGE_EMPLOYER_ACCOUNT]}>
              <ATSIntegrationPage />
            </ProtectedRoute>
          }
        />
      </Route>
    </Route>
    <Route path="employers/:id" element={<EmployerProfilePage />} />
    <Route path="jobs" element={<Outlet />}>
      <Route index element={<JobsListing />} />
      <Route path="AITraining" element={<DataLabelingJobsListing />} />
      <Route
        path="add_new/*"
        element={
          <ProtectedRoute rules={[...ACCESS_RULE.ACCOUNT_ALL_SET]}>
            <Outlet />
          </ProtectedRoute>
        }
      >
        {CreateJobRoutes}
      </Route>
      <Route
        path=":id"
        element={
          <ProtectedRoute rules={[]}>
            <JobDetailsPage />
          </ProtectedRoute>
        }
      >
        <Route
          path="invite_matched_talent"
          element={
            <ProtectedRoute rules={[...ACCESS_RULE.ACCOUNT_ALL_SET]}>
              <InviteTalentDrawer />
            </ProtectedRoute>
          }
        />
      </Route>
      <Route path=":id">
        <Route
          path="invite_talent"
          element={
            <ProtectedRoute rules={[ACCESS_RULE.AUTHENTICATED]}>
              <InviteTalentPage />
            </ProtectedRoute>
          }
        />
        <Route path="edit/*" element={<Outlet />}>
          {EditJobRoutes}
        </Route>
        <Route
          path="proposals"
          element={
            <ProtectedRoute rules={[ACCESS_RULE.AUTHENTICATED]}>
              <JobBidsPage />
            </ProtectedRoute>
          }
        >
          <Route path=":bidId" element={<BidDrawer />} />
        </Route>
        <Route
          path="interviews"
          element={
            <ProtectedRoute rules={[ACCESS_RULE.AUTHENTICATED]}>
              <InterviewPage />
            </ProtectedRoute>
          }
        >
          <Route path=":bidId" element={<BidDrawer isInterviewingTab />} />
        </Route>
        <Route
          path="interview-questions"
          element={
            <ProtectedRoute rules={[ACCESS_RULE.AUTHENTICATED]}>
              <InterviewQuestionsPage />
            </ProtectedRoute>
          }
        />
        <Route path="proposals">
          <Route
            path="new"
            element={
              <ProtectedRoute rules={[ACCESS_RULE.NOT_EMPLOYER]}>
                <JobNewBidPage />
              </ProtectedRoute>
            }
          />
          <Route
            path=":bidId/edit"
            element={
              <ProtectedRoute rules={[ACCESS_RULE.HAS_FREELANCER]}>
                <JobEditBidPage />
              </ProtectedRoute>
            }
          />
        </Route>
        <Route
          path="proposals/:bidId/rejection_feedback"
          element={<RejectionFeedbackPage />}
        />
        <Route path="offers/*" element={<Outlet />}>
          {OffersRoutes}
        </Route>
      </Route>
    </Route>
    <Route path="jobs-list" element={<EmbeddedJobsListPage />} />
    <Route
      path="bookmarks"
      element={
        <ProtectedRoute
          rules={[ACCESS_RULE.AUTHENTICATED, ACCESS_RULE.HAS_FREELANCER]}
        >
          <BookmarksPage />
        </ProtectedRoute>
      }
    >
      <Route index element={<Navigate to="jobs" replace />} />
      <Route path="jobs" element={<JobsBookmarksPage />} />
    </Route>
    <Route
      path="rosters"
      element={
        <ProtectedRoute rules={[ACCESS_RULE.IS_NODE_STAFF]}>
          <Outlet />
        </ProtectedRoute>
      }
    >
      <Route index element={<RostersListPage />} />
      <Route path=":id" element={<RosterDetailPage />} />
    </Route>
    <Route
      path="role-jobs"
      element={
        <ProtectedRoute rules={[ACCESS_RULE.HAS_FREELANCER]}>
          <RoleJobsPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="search-jobs"
      element={
        <ProtectedRoute rules={[ACCESS_RULE.AUTHENTICATED]}>
          <SearchJobsPage />
        </ProtectedRoute>
      }
    />

    <Route
      path="settings"
      element={
        <ProtectedRoute rules={[...ACCESS_RULE.ACCOUNT_ALL_SET]}>
          <Outlet />
        </ProtectedRoute>
      }
    >
      <Route index element={<Navigate to="account_info" />} />
      <Route path="account_info" element={<SettingsAccountInfoPage />} />
      <Route path="notifications" element={<SettingsNotifications />} />
      <Route path="privacy_security" element={<SettingsPrivacySecurity />} />
      <Route path="payments" element={<SettingsPayments />} />
    </Route>
    <Route
      path="messages"
      element={
        <ProtectedRoute rules={[...ACCESS_RULE.ACCOUNT_ALL_SET]}>
          <MessagesProvider>
            <MessagesPage />
          </MessagesProvider>
        </ProtectedRoute>
      }
    />
    <Route
      path="onboarding"
      element={
        <ProtectedRoute rules={[...EMPLOYER_MAIN_ACCESS_RULE]}>
          <Outlet />
        </ProtectedRoute>
      }
    >
      <Route index element={<Navigate to="organization" />} />
      <Route path="organization" element={<OnboardingOrganizationPage />} />
    </Route>
    <Route
      path="complete_profile"
      element={
        <ProtectedRoute rules={[...ACCESS_RULE.ACCOUNT_ALL_SET]}>
          <CompleteProfilePage />
        </ProtectedRoute>
      }
    />
    <Route
      path="token"
      element={<Navigate to="https://www.usebraintrust.com/btrust-token" />}
    />
    <Route path="ai_interview/:id" element={<AIInterviewRedirect />} />
    <Route path="confirm" element={<ConfirmPage />} />
    <Route path="429" element={<TooManyRequestsPage />} />
    <Route path="page-not-found" element={<PageNotFoundPage />} />
    <Route path="access-denied" element={<AccessDenied />} />
    <Route path="*" element={<UnknownRoute to="/page-not-found" />} />
  </Route>
);

export const getRouter = () =>
  createBrowserRouter(createRoutesFromElements(routes()), {
    window,
    future: {
      v7_relativeSplatPath: true,
      v7_fetcherPersist: true,
      v7_normalizeFormMethod: true,
      v7_partialHydration: true,
      v7_skipActionErrorRevalidation: true,
    },
  });
